import { useEffect, useRef } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../utils/api';
import {
  fetchCurrentLocation,
  getStoredLocation,
  requestLocationPermissionOnce,
  watchLiveLocation,
} from '../utils/locationPermissionUtils';

const HEARTBEAT_MS = 25000;

async function pingPresence(token, lat, lng) {
  const body = {};
  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    body.latitude = lat;
    body.longitude = lng;
  }

  try {
    await axios.post(API_ENDPOINTS.pingLiveLocation, body, {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (err) {
    console.warn('Presence ping failed', err?.response?.status || err?.message || err);
  }
}

/**
 * Capture GPS and send it to the server so the profile appears on the map.
 */
export function useLiveLocationPing() {
  const lastPingRef = useRef(0);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return undefined;

    let stopWatch = () => {};
    let heartbeat = null;
    let cancelled = false;

    const sendCoords = async (lat, lng) => {
      const now = Date.now();
      if (Number.isFinite(lat) && now - lastPingRef.current < 4000) return;
      lastPingRef.current = now;
      try {
        await pingPresence(token, lat, lng);
      } catch (err) {
        console.warn('Presence ping failed', err?.message || err);
      }
    };

    const start = async () => {
      await requestLocationPermissionOnce();
      if (cancelled) return;

      try {
        const coords = await fetchCurrentLocation({ silent: true });
        const [lat, lng] = coords.split(',').map(Number);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          await sendCoords(lat, lng);
        }
      } catch {
        const stored = getStoredLocation();
        if (stored?.lat != null && stored?.lng != null) {
          await sendCoords(stored.lat, stored.lng);
        } else {
          await pingPresence(token);
        }
      }

      if (cancelled) return;

      stopWatch = watchLiveLocation(
        ({ lat, lng }) => {
          if (!cancelled) sendCoords(lat, lng);
        },
        () => {}
      );
    };

    start();
    heartbeat = setInterval(() => {
      const stored = getStoredLocation();
      if (stored?.lat != null && stored?.lng != null) {
        pingPresence(token, stored.lat, stored.lng);
      } else {
        pingPresence(token);
      }
    }, HEARTBEAT_MS);

    return () => {
      cancelled = true;
      stopWatch();
      if (heartbeat) clearInterval(heartbeat);
    };
  }, []);
}

export default useLiveLocationPing;
